<script setup lang="ts">

import { ref, computed, watch, onMounted } from 'vue'
import RoomieChipSelector from '@lahaus-roomie/roomie/src/components/RoomieChipSelector/index.vue'
import { parseZonesToItems, removeHubZonesFromCurrentSelection } from './utils'
import AppSlider from '@/components/AppSlider/index.vue'
import DropdownChecklist from '@/components/DropdownChecklist/index.vue'
import { useLocationsStore } from '@/stores/locationsStore'

import type { Props } from '@/components/SearchFilters/ModalContent/ClustersSelector/types'
import type { CollapsibleItem } from '@/components/DropdownChecklist/types'
import type { BusinessHub } from '@/utils/locations/types'

import type { SearchFilters } from '@/components/SearchFilters/types'

import './_index.scss'
withDefaults(defineProps<Props>(), {
  id: undefined,
  enableDefaults: false,
  dataAttributes: {
    dataLh: { P: 'unknown', Z: 'unknown' }
  }
})

const emit = defineEmits(['change'])
const model = defineModel<SearchFilters>({ required: true })

const { t } = useI18n()
const { $useLhId } = useNuxtApp()

const locationsStore = useLocationsStore()

const availableHubs = locationsStore.locationsQueryUtils.getAllAvailableBusinessHubs()

const selectedHubs = ref<string[]>([])
const selectedZones = ref<CollapsibleItem[]>([])
const areThereSelectedHubs = computed(() => selectedHubs.value.length > 0)

const handleHubClick = (hub: BusinessHub) => {
  if (selectedHubs.value.includes(hub.value)) {
    const hubIndex = selectedHubs.value.indexOf(hub.value)
    selectedHubs.value.splice(hubIndex, 1)
    selectedZones.value = removeHubZonesFromCurrentSelection(selectedZones.value, hub.value, locationsStore.locationsQueryUtils)
    return
  }

  const hubZones = parseZonesToItems(locationsStore.locationsQueryUtils.getZonesByBusinessHubValue(hub.value))
  selectedHubs.value.push(hub.value)
  selectedZones.value.push(...hubZones)
}

const itemsBySelectedHubs = computed(() => {
  return parseZonesToItems(selectedHubs.value.map(hubValue => locationsStore.locationsQueryUtils.getZonesByBusinessHubValue(hubValue)).flat())
})

const zoneItemsToShow = computed(() => itemsBySelectedHubs.value.map(({ label, value, children }) => {
  const translatedLabel = `
    <div class="py-8 w-full">
      <p class="body-2-m truncate">${label}</p>
      <span class="body-3-m">(${t('clustersSelector.neighborhoodsCount', { count: children.length }, children.length)})</span>
    </div>
  `

  return ({ label: translatedLabel, value, children })
}))

const filterHubsByNeighborhoods = (hubs: CollapsibleItem[], selectedNeighborhoods: string[]) => {
  return hubs
    .map((hub) => {
      const matchingNeighborhoods = hub.children.filter(child =>
        selectedNeighborhoods.includes(child.value)
      )

      if (matchingNeighborhoods.length > 0) {
        return {
          ...hub,
          children: matchingNeighborhoods
        }
      }

      return null
    })
    .filter((hub): hub is CollapsibleItem => hub !== null)
}

onMounted(() => {
  if (model.value.businessHubs.length > 0) selectedHubs.value = [...model.value.businessHubs]
  if (model.value.neighborhoods.length > 0) {
    selectedZones.value = filterHubsByNeighborhoods(itemsBySelectedHubs.value, model.value.neighborhoods)
  }
})

watch(selectedHubs, (newHubs) => {
  model.value.businessHubs.splice(0, model.value.businessHubs.length, ...newHubs)
  model.value.clusters = []
  emit('change', { isSelected: model.value.businessHubs.length > 0, key: 'businessHubs' })
}, { deep: true })

watch(selectedZones, (newValue) => {
  const allNeighborhoodValues = newValue.map(({ children }) => children.map(({ value }) => value)).flat()
  model.value.neighborhoods.splice(0, model.value.neighborhoods.length, ...allNeighborhoodValues)
  model.value.clusters = []
  emit('change', { isSelected: model.value.neighborhoods.length > 0, key: 'neighborhoods' })
}, { deep: true })

watch(model, (newValue): void => {
  if (newValue.businessHubs.length === 0) {
    selectedHubs.value = []
    selectedZones.value = []
  }
})
</script>

<script lang="ts">
export default {
  name: 'ClustersSelector'
}
</script>

<template>
  <div class="clusters-selector">
    <h4 class="text-pine-600 body-2-sb lg:body-1-sb mb-8">
      {{ t('businessHubSelector.label') }}
    </h4>

    <AppSlider
      :id="`${id}-hub-chips-slider`"
      class="w-full"
      lateral-paddings="hidden"
      :padding-right="true">
      <RoomieChipSelector
        v-for="(hub, index) in availableHubs"
        :id="`${id}-hub-chip-${index}`"
        :key="hub.value"
        class="chip-selector my-4"
        :data-lh-id="$useLhId({ ...dataAttributes.dataLh, A: !selectedHubs.includes(hub.value) ? 'deseleccionar_zona' : 'seleccionar_zona', C: hub.alias || hub.name })"
        :is-selected="selectedHubs.includes(hub.value)"
        @click="handleHubClick(hub)">
        <template #content>
          <span class="body-1-m whitespace-nowrap pointer-events-none">
            {{ hub.alias || hub.name }}
          </span>
        </template>
      </RoomieChipSelector>
    </AppSlider>

    <div
      v-if="areThereSelectedHubs"
      class="clusters-selector__dropdowns-group mt-16">
      <h4 class="text-pine-600 body-2-sb lg:body-1-sb mb-8">
        {{ t('clustersSelector.label') }}
      </h4>

      <DropdownChecklist
        :id="`${id}-clusters-selector`"
        v-model="selectedZones"
        :label="t('clustersSelector.selectedLabel', { count: selectedZones.length })"
        :placeholder="t('clustersSelector.placeholder')"
        :items="zoneItemsToShow"
        :data-attributes="dataAttributes"
        can-select-all
        class="w-full" />
    </div>
  </div>
</template>

<i18n src="./i18n.json" />
